import { DomainDataType } from 'types/domain';
import { Domain } from '@monsido/modules/models/api/domain';

export interface ParsedImportFile {
    isValid: boolean;
    domains: CleanedDomainData[];
    totalCount: number;
    errors: string[];
    exportedAt?: string;
    exportedBy?: string;
}

export interface CleanedDomainData extends Omit<DomainDataType,
    | 'customer_id'
    | 'customer'
    | 'crawl_history'
    | 'crawl_status'
    | 'last_scan'
    | 'crawled_pages'
    | 'token'
    | 'running_groups'
> {
    exportVersion?: string;
}

export interface ImportProgress {
    current: number;
    total: number;
    message: string;
    currentDomain?: string;
    phase?: 'validation' | 'creation' | 'completed';
    estimatedTimeRemaining?: number;
    errorsEncountered?: number;
}

export interface ImportConfiguration {
    skipDomainGroups: boolean;
    skipPathConstraints: boolean;
    skipLinkExcludes: boolean;
    batchSize?: number;
    retryFailedDomains?: boolean;
    continueOnError?: boolean;
}

export interface ImportResult {
    success: boolean;
    domain: CleanedDomainData;
    error?: string;
    createdDomain?: Domain;
    errorType?: 'validation' | 'network' | 'server' | 'permission' | 'unknown';
    retryable?: boolean;
    validationWarnings?: string[];
}

export interface ImportSummary {
    totalDomains: number;
    successCount: number;
    failureCount: number;
    results: ImportResult[];
    startedAt: Date;
    completedAt?: Date;
    errorsByType?: { [key: string]: number };
    retryableErrors?: number;
    averageCreationTime?: number;
    validationSummary?: {
        totalValidated: number;
        validDomains: number;
        invalidDomains: number;
        warningCount: number;
    };
}

export enum WizardStep {
    UPLOAD = 'upload',
    CONFIGURE = 'configure',
    CONFIRM = 'confirm',
    PROGRESS = 'progress',
    RESULTS = 'results',
}

export interface ExportProgress {
    current: number;
    total: number;
    message: string;
}

export interface DomainValidationResult {
    isValid: boolean;
    url: string;
    title?: string;
    errors: string[];
    warnings: string[];
    suggestions?: string[];
    fieldErrors?: Record<string, string[]>;
}

export interface ValidationSummary {
    totalDomains: number;
    validDomains: number;
    invalidDomains: number;
    duplicates: number;
    errors: string[];
    warningCount?: number;
    criticalErrors?: string[];
}

export interface DomainCreationRequest {
    url: string;
    name: string;
    language: string;
    tags?: string[];
    description?: string;
    createdBy?: string;
    cms?: string;
}

export interface DomainCreationResult {
    success: boolean;
    domain?: DomainDataType;
    error?: string;
    validationErrors?: string[];
    errorType?: 'validation' | 'network' | 'server' | 'permission' | 'unknown';
    retryable?: boolean;
}

export interface BulkCreationProgress {
    current: number;
    total: number;
    completed: number;
    failed: number;
    currentDomainName?: string;
    estimatedTimeRemaining?: number;
    errors: Array<{ domain: string; error: string; retryable: boolean }>;
}

export interface CreationMetrics {
    totalRequests: number;
    successfulCreations: number;
    failedCreations: number;
    averageResponseTime: number;
    errorsByType: { [key: string]: number };
}

export interface DomainActionProgress {
    current: number;
    total: number;
    message: string;
    isActive: boolean;
}
